import { useQueryClient } from '@tanstack/react-query';
import { FC, useMemo } from 'react';

import { GiveBoxEvent } from '@bonusly/common-give-box';
import { VStack } from '@bonusly/design-system';
import { useEventListener } from '@bonusly/events';
import { Permission, ProtectedContent } from '@bonusly/who-am-i';

import { DirectReport } from '../../../../types/user';
import { PersonList } from '../../../bonus-feed/features/recognition/components/tracker-widget/components/person-list';
import { ProgressBar } from '../../../bonus-feed/features/recognition/components/tracker-widget/components/progress-bar';
import { QueryKey } from '../../../bonus-feed/features/recognition/components/tracker-widget/hooks';
import { Person } from '../../../bonus-feed/features/recognition/components/tracker-widget/types';
import { DirectReportList, EnrichedDirectReport } from './direct-report-list';

interface TeamTrackerProps {
	directReports: DirectReport[];
	people?: Person[];
}

export const TeamTracker: FC<TeamTrackerProps> = ({ directReports, people }) => {
	const queryClient = useQueryClient();

	// Invalidate team tracker query when recognition is given/deleted
	// This ensures the recognition count and last recognized at update automatically
	useEventListener(GiveBoxEvent.Created, () => {
		queryClient.invalidateQueries([QueryKey.TeamTracker]);
	});

	useEventListener(GiveBoxEvent.Deleted, () => {
		queryClient.invalidateQueries([QueryKey.TeamTracker]);
	});

	// NOTE: need to add recognition count and last recognized at to the direct reports
	// using the team tracker data. When people data is not yet loaded, fall back to
	// directReports data to avoid showing an empty list while loading.
	const updatedDirectReports: EnrichedDirectReport[] = useMemo(() => {
		if (!people) {
			// Fall back to directReports data when team tracker hasn't loaded yet
			return directReports.map((report) => ({
				...report,
				lastRecognizedAt: null,
				recognitionCount: report.recognitionCount,
			}));
		}

		const peopleById = new Map(people.map((person) => [person.id, person]));

		return directReports.map((report) => {
			const person = peopleById.get(report.id);
			return {
				...report,
				lastRecognizedAt: person?.last_recognized_at ?? null,
				recognitionCount: person?.recognition_count ?? report.recognitionCount,
			};
		});
	}, [directReports, people]);

	const hasDirectReports = directReports.length > 0;

	return (
		<ProtectedContent permissions={Permission.CanGiveRecognition}>
			<VStack data-testid="team-tracker" px={2} py={4} gap={4} alignItems="stretch" position="relative">
				<ProgressBar people={people || []} />

				{/* MANAGER - DIRECT REPORTS WITH SECTIONS */}
				{hasDirectReports && <DirectReportList directReports={updatedDirectReports} />}

				{/* NON-MANAGER - TEAM TRACKER LIST */}
				{!hasDirectReports && people && <PersonList people={people} />}

				{/* Canvas for confetti animation */}
				<canvas
					id="progressBarWrapper"
					style={{
						position: 'absolute',
						width: '100%',
						height: '100%',
						top: 0,
						left: 0,
						pointerEvents: 'none',
					}}
				/>
			</VStack>
		</ProtectedContent>
	);
};
